import React, { useCallback, useState, useMemo } from 'react';
import { injectIntl } from 'react-intl';

import PermissionLogo from 'components/PermissionLogo';

import './style.less';

import {
  Col,
  Row,
  Modal,
  Tabs,
  Checkbox,
} from 'antd';
import CustomWarningModal from 'components/CustomWarningModal';

const BOOLEAN_PERMISSIONS = {
  community: [
    'can_read_initiatives',
    'can_read_reports',
    'can_manage_participation',
    'can_manage_collaborations',
    'can_manage_proposals',
    'can_manage_initiatives',
    'can_manage_volunteers',
  ],
  general: [
    'can_manage_configuration',
    'can_manage_permissions',
  ],
};

const MANDATORY_PERMISSIONS = {
  community: ['can_read_initiatives'],
  general: [],
};

const ManagePermissions = ({
  intl,
  visible,
  pushing,
  onSubmit,
  onCancel,
  member,
  organization,
  allowedProducts,
  currentUser,
  is_system
}) => {
  const t = intl.messages;
  
  const [values, setValues] = useState(
    member.details?.is_auditor
      ? { ...member }
      : {
          details: {...(member.details || {})},
          community_permission: {
            ...member.community_permission,
            can_read_initiatives: true,
          },
          general_permission: member.general_permission,
        }
    );
  const [showKPIDataOwnershipWarning, setShowKPIDataOwnershipWarning] = useState(false);

  const togglePermission = useCallback(
    (app, permission) => {
      const newAppState = {
        ...values[`${app}_permission`],
        [ permission ]: !values[`${app}_permission`][permission],
      };

      setValues({
        ...values,
        [ `${app}_permission` ]: newAppState,
      })
    },
    [values]
  );

  const handleSubmit = useCallback(
    () => {
      if (
        !showKPIDataOwnershipWarning &&
        member?.kpi_data_ownership?.length
      ) {
        setShowKPIDataOwnershipWarning(true);
      } else {
        onSubmit(values);
        onCancel();
      }
    },
    [
      onSubmit,
      onCancel,
      values,
      member,
      showKPIDataOwnershipWarning,
    ]
  );

  const permissionDisabled = useCallback((app, permission) => {
    const hasPermission =  MANDATORY_PERMISSIONS[app].includes(permission);
    return is_system ? 
      hasPermission : 
      (hasPermission || !((currentUser[`${app}_permission_inherit`] || {})[permission] || currentUser[`${app}_permission`][permission]));
  }, [currentUser, is_system]);

  const mandatoryPermissions = useMemo(
    () => {
      let permissions = MANDATORY_PERMISSIONS;
      if (values.details?.is_auditor) {
        permissions = {
          community: [],
          general: [],
        };
      }
      return permissions;
    }, [
      values,
    ]
  );

  const disabledPermissions = useMemo(
    () => {
      if (values.details?.is_auditor) {
        return {
          community: [
            'can_read_initiatives',
            'can_read_reports',
            'can_manage_participation',
            'can_manage_collaborations',
            'can_manage_proposals',
            'can_manage_initiatives',
            'can_manage_volunteers',
          ],
          general: [
            'can_manage_configuration',
            'can_manage_permissions',
          ],
        };
      }
      return {};
    },
    [
      values.details,
    ]
  );

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={t.organization_profile_save}
      title={t.organization_profile_manage_permissions}
      className="ManagePermissions"
      okButtonProps={{ disabled: pushing }}
      cancelButtonProps={{ disabled: pushing }}
      closable={!pushing}
    >
      <Row>
        <Col span={24}>
          <Tabs>
            <Tabs.TabPane
              tab={ t.organization_profile_permissions_general }
              key="general"
            >
              <Row gutter={10}>
                {
                  BOOLEAN_PERMISSIONS.general.map(permission => (
                    <Col key={permission} span={24}>
                      <Checkbox
                        checked={values.general_permission[permission]}
                        onChange={() => togglePermission('general', permission)}
                        disabled={ mandatoryPermissions.general.includes(permission) || disabledPermissions.general?.includes(permission) || permissionDisabled('general', permission) }
                      >
                        <PermissionLogo
                          className="ManagePermissions-permission-logo"
                          permission={permission}
                          app="general"
                        />
                        { t[`permission_general_${permission}`] }
                      </Checkbox>
                    </Col>
                  ))
                }
              </Row>
            </Tabs.TabPane>
            { allowedProducts.includes('community')
              ? <Tabs.TabPane
                  tab={ t.organization_profile_permissions_community }
                  key="community"
                >
                  <Row gutter={10}>
                    {
                      BOOLEAN_PERMISSIONS.community.map(permission => (
                        <Col key={permission} span={24}>
                          <Checkbox
                            checked={values.community_permission[permission]}
                            onChange={() => togglePermission('community', permission)}
                            disabled={ mandatoryPermissions.community.includes(permission) || disabledPermissions.community?.includes(permission) || permissionDisabled('community', permission) }
                          >
                            <PermissionLogo
                              className="ManagePermissions-permission-logo"
                              permission={permission}
                              app="community"
                            />
                            { t[`permission_community_${permission}`] }
                          </Checkbox>
                        </Col>
                      ))
                    }
                  </Row>
                </Tabs.TabPane>
              : null
            }
          </Tabs>
        </Col>
      </Row>
      <CustomWarningModal
        showModal={showKPIDataOwnershipWarning}
        onOk={handleSubmit}
        onCancel={() => setShowKPIDataOwnershipWarning(false)}
        onOkText={intl.formatMessage({id: 'organization_profile_change_permission_warning_modal_button_ok'})}
        onCancelText={intl.formatMessage({id: 'organization_profile_change_permission_warning_modal_button_cancel'})}
        title={`${intl.formatMessage({id: 'organization_profile_change_permission_warning_modal_title'})} ${member.name}`}
        content={
          <Row gutter={[0, 10]}>
            <Col span={24}>
              {intl.formatMessage({id: 'organization_profile_change_permission_warning_modal_content'})}
            </Col>
            <Col span={24}>
              <b>
                {intl.formatMessage({id: 'organization_profile_change_permission_warning_modal_content_bold'})}
              </b>
            </Col>
          </Row>
        }
        icon="exclamation-triangle"
      />
    </Modal>
  )
}

export default injectIntl(ManagePermissions);
